/**
 * Maps primary and secondary lookup data into structured objects for efficient access.
 *
 * @param {Object} params - The parameters object.
 * @param {Array<Object>} params.primaryLookupData - Array of primary lookup data objects, each containing `siteUUID`, `tableName`, and `results`.
 * @param {Object} params.secondaryLookupFields - Object mapping siteUUIDs to resources, where each resource contains tables and their fields with linking metadata.
 * @returns {Object} An object containing:
 *   - {Object} lookupDataMappedToTable: Maps siteUUIDs to table names and their results.
 *   - {Object} secondaryLookedupData: Maps siteUUIDs to tables, then to objects keyed by the resource linking key.
 */
export default function mapLookupData({ primaryLookupData, secondaryLookupFields }) {
    const lookupDataMappedToTableWithSite = primaryLookupData.reduce((acc, tableResult) => {
        if (acc[tableResult.siteUUID] == undefined) {
            acc[tableResult.siteUUID] = {};
        }

        acc[tableResult.siteUUID][tableResult.tableName] = tableResult.results;
        return acc;
    }, {});

    // Group the lookup fields by siteUUID and then by table name
    const lookupFieldsMapped = Object.keys(secondaryLookupFields).reduce((siteAcc, siteUUID) => {
        const resources = secondaryLookupFields[siteUUID]
        const resourcesList = Object.keys(resources);

        const lookedUpDataForThisSite = resourcesList.reduce((acc, table) => {
            const fields = resources[table];

            Object.keys(fields).reduce((innerAcc, field) => {
                const fieldData = fields[field];

                if (innerAcc[field] == undefined) {
                    innerAcc[field] = {
                        linksToSiteUUID: fieldData.linksToSiteUUID,
                        tableName: fieldData.linksToResource,
                        resourceLinkingKey: fieldData.resourceLinkingKey,
                    }
                }

                return innerAcc;
            }, acc);

            return acc;
        }, {});

        siteAcc[siteUUID] = lookedUpDataForThisSite;

        return siteAcc;
    }, {});

    const resultsMappedToTable = primaryLookupData.reduce((acc, tableResult) => {
        if (acc[tableResult.siteUUID] == undefined) {
            acc[tableResult.siteUUID] = {};
        }

        acc[tableResult.siteUUID][tableResult.tableName] = tableResult.results;
        return acc;
    }, {});

    const computedLookupData = Object.keys(lookupFieldsMapped).reduce((acc, siteUUID) => {
        const fields = lookupFieldsMapped[siteUUID];

        Object.keys(fields).reduce((acc, fieldName) => {
            const tableName = fields[fieldName].tableName;
            const tableData = resultsMappedToTable[siteUUID][tableName];

            if (acc[siteUUID] == undefined) {
                acc[siteUUID] = {};
            }

            acc[siteUUID][fieldName] = tableData.reduce((innerAcc, row) => {
                const linkingKey = fields[fieldName].resourceLinkingKey;

                innerAcc[row[linkingKey]] = row;
                return innerAcc;
            }, {});

            return acc;
        }, acc);

        return acc;
    }, {});

    // const secondaryComputedLookupData = Object.keys(secondaryLookupFields).reduce((appsAcc, appUUID) => {
    //     const tables = secondaryLookupFields[appUUID];

    //     appsAcc[appUUID] = Object.keys(tables).reduce((tablesAcc, table) => {
    //         const fields = tables[table];

    //         Object.keys(fields).forEach((field) => {
    //             const {
    //                 fieldName,
    //                 linksToResource,
    //                 resourceDisplayKey,
    //                 resourceDisplayKeysMultiple,
    //                 resourceLinkingKey,
    //                 linksToSiteUUID,
    //             } = fields[field];

    //             const dataToMap = resultsMappedToTable?.[linksToSiteUUID]?.[linksToResource];

    //             if (dataToMap) {
    //                 const mappedData = dataToMap.reduce((acc, row) => {
    //                     const key = row[resourceLinkingKey];

    //                     if (acc[key] == undefined) {
    //                         acc[key] = row;
    //                     }

    //                     return acc;
    //                 }, {});

    //                 tablesAcc[linksToResource] = mappedData;
    //             }
    //         });

    //         return tablesAcc;
    //     }, {});

    //     return appsAcc;
    // }, {});


    const applications = Object.keys(lookupFieldsMapped);

    const secondaryLookedupData = applications.reduce((acc, siteUUID) => {
        const resourcesForThisSite = lookupFieldsMapped[siteUUID];

        const secondaryLookedupData = Object.keys(resourcesForThisSite).reduce((resourcesAcc, fieldName) => {
            const tableName = resourcesForThisSite[fieldName].tableName;

            resourcesAcc[tableName] = Object.values(lookupDataMappedToTableWithSite[siteUUID][tableName]).reduce((innerAcc, row) => {
                const linkingKey = resourcesForThisSite[fieldName].resourceLinkingKey;

                innerAcc[row[linkingKey]] = row;
                return innerAcc;
            }, {});

            return resourcesAcc;
        }, {});

        acc[siteUUID] = secondaryLookedupData;

        return acc;
    }, {});

    return {
        computedLookupData,

        lookupDataMappedToTable: lookupDataMappedToTableWithSite,
        secondaryLookedupData,
    }
}